
import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ManualCrop, Bbox } from '../types';

// pdf.js type declaration
declare const pdfjsLib: any;

interface ManualCropViewProps {
    file: File;
    onFinish: (crops: ManualCrop[]) => void;
    onCancel: () => void;
}

type InteractionMode = 'none' | 'drawing' | 'moving' | 'resizing';
type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const HANDLE_SIZE = 8; // pixels

const ManualCropView: React.FC<ManualCropViewProps> = ({ file, onFinish, onCancel }) => {
    const [pdfDoc, setPdfDoc] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    
    const [mode, setMode] = useState<'problem' | 'explanation'>('problem');
    const [crops, setCrops] = useState<ManualCrop[]>([]);
    
    // Interaction State
    const [interactionMode, setInteractionMode] = useState<InteractionMode>('none');
    const [selectedCropIndex, setSelectedCropIndex] = useState<number | null>(null);
    const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null);
    
    // Coordinates for drag operations
    const [startPos, setStartPos] = useState<{ x: number, y: number } | null>(null);
    const [currentPos, setCurrentPos] = useState<{ x: number, y: number } | null>(null);
    const [initialBbox, setInitialBbox] = useState<Bbox | null>(null); // Snapshot for move/resize

    const canvasRef = useRef<HTMLCanvasElement>(null); // PDF Render Layer
    const overlayRef = useRef<HTMLCanvasElement>(null); // Drawing Layer
    const containerRef = useRef<HTMLDivElement>(null);

    // Load PDF
    useEffect(() => {
        const loadPdf = async () => {
            setIsLoading(true);
            try {
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
            } catch (e) {
                console.error("Failed to load PDF:", e);
            } finally {
                setIsLoading(false);
            }
        };
        loadPdf();
    }, [file]);

    // Render Page
    useEffect(() => {
        const renderPage = async () => {
            if (!pdfDoc || !canvasRef.current) return;
            
            const page = await pdfDoc.getPage(currentPage);
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (!context) return;

            const viewport = page.getViewport({ scale: 1.5 }); // Good quality scale
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.style.width = '100%';
            canvas.style.height = 'auto';

            // Also resize overlay to match
            if (overlayRef.current) {
                overlayRef.current.width = viewport.width;
                overlayRef.current.height = viewport.height;
                overlayRef.current.style.width = '100%';
                overlayRef.current.style.height = 'auto';
            }

            await page.render({ canvasContext: context, viewport }).promise;
            
            // Redraw existing crops for this page
            drawOverlay();
        };
        renderPage();
    }, [pdfDoc, currentPage]);

    // --- Helper Functions for Geometry ---

    const getCanvasMetrics = () => {
        const canvas = overlayRef.current;
        if (!canvas) return { width: 0, height: 0 };
        return { width: canvas.width, height: canvas.height };
    };

    const normalizeRect = (x: number, y: number, w: number, h: number, cw: number, ch: number): Bbox => {
        // Ensure x, y are top-left and w, h are positive
        let nx = x;
        let ny = y;
        let nw = w;
        let nh = h;

        if (nw < 0) { nx += nw; nw = Math.abs(nw); }
        if (nh < 0) { ny += nh; nh = Math.abs(nh); }

        return {
            x_min: Math.max(0, Math.min(1, nx / cw)),
            y_min: Math.max(0, Math.min(1, ny / ch)),
            x_max: Math.max(0, Math.min(1, (nx + nw) / cw)),
            y_max: Math.max(0, Math.min(1, (ny + nh) / ch)),
        };
    };

    // Get handle rectangles in pixels
    const getHandleRects = (bbox: Bbox, cw: number, ch: number) => {
        const x = bbox.x_min * cw;
        const y = bbox.y_min * ch;
        const w = (bbox.x_max - bbox.x_min) * cw;
        const h = (bbox.y_max - bbox.y_min) * ch;
        const hs = HANDLE_SIZE;
        const hhs = hs / 2;

        return {
            nw: { x: x - hhs, y: y - hhs, w: hs, h: hs },
            n:  { x: x + w/2 - hhs, y: y - hhs, w: hs, h: hs },
            ne: { x: x + w - hhs, y: y - hhs, w: hs, h: hs },
            e:  { x: x + w - hhs, y: y + h/2 - hhs, w: hs, h: hs },
            se: { x: x + w - hhs, y: y + h - hhs, w: hs, h: hs },
            s:  { x: x + w/2 - hhs, y: y + h - hhs, w: hs, h: hs },
            sw: { x: x - hhs, y: y + h - hhs, w: hs, h: hs },
            w:  { x: x - hhs, y: y + h/2 - hhs, w: hs, h: hs },
        };
    };

    const isPointInRect = (px: number, py: number, rect: { x: number, y: number, w: number, h: number }) => {
        return px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h;
    };

    // Draw Overlay (Crops + Handles + Interactions)
    const drawOverlay = useCallback(() => {
        const canvas = overlayRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 1. Draw all crops
        crops.forEach((crop, index) => {
            if (crop.pageIndex !== currentPage) return;

            const isSelected = index === selectedCropIndex;
            const { x_min, y_min, x_max, y_max } = crop.bbox;
            const x = x_min * canvas.width;
            const y = y_min * canvas.height;
            const w = (x_max - x_min) * canvas.width;
            const h = (y_max - y_min) * canvas.height;

            const color = crop.type === 'problem' ? '#3b82f6' : '#ef4444';
            const bgColor = crop.type === 'problem' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(239, 68, 68, 0.2)';

            ctx.strokeStyle = color;
            ctx.lineWidth = isSelected ? 3 : 2;
            ctx.setLineDash([]);
            ctx.strokeRect(x, y, w, h);
            
            ctx.fillStyle = bgColor;
            ctx.fillRect(x, y, w, h);

            // Label
            ctx.font = 'bold 14px sans-serif';
            ctx.fillStyle = color;
            ctx.fillText(crop.label || `#${index + 1}`, x, y - 6);

            // Handles if selected
            if (isSelected) {
                const handles = getHandleRects(crop.bbox, canvas.width, canvas.height);
                ctx.fillStyle = '#ffffff';
                ctx.strokeStyle = color;
                ctx.lineWidth = 1;

                Object.values(handles).forEach(rect => {
                    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
                    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
                });
            }
        });

        // 2. Draw Drawing Box (New)
        if (interactionMode === 'drawing' && startPos && currentPos) {
            const x = Math.min(startPos.x, currentPos.x) * canvas.width;
            const y = Math.min(startPos.y, currentPos.y) * canvas.height;
            const w = Math.abs(currentPos.x - startPos.x) * canvas.width;
            const h = Math.abs(currentPos.y - startPos.y) * canvas.height;

            ctx.strokeStyle = mode === 'problem' ? '#3b82f6' : '#ef4444';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 3]);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);
        }
    }, [crops, currentPage, interactionMode, startPos, currentPos, mode, selectedCropIndex]);

    useEffect(() => {
        drawOverlay();
    }, [drawOverlay]);

    const getNormalizedPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = overlayRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        
        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

        return {
            x: (clientX - rect.left) / rect.width,
            y: (clientY - rect.top) / rect.height
        };
    };

    const getPixelPos = (e: React.MouseEvent) => {
        const canvas = overlayRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        
        // Get mouse position relative to the canvas element (CSS pixels)
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Scale to canvas internal resolution
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return { x: x * scaleX, y: y * scaleY };
    };

    // Mouse Down Logic
    const handleMouseDown = (e: React.MouseEvent) => {
        const canvas = overlayRef.current;
        if (!canvas) return;
        
        const { x, y } = getPixelPos(e);
        const nPos = getNormalizedPos(e);

        // 1. Check handles of currently selected crop
        if (selectedCropIndex !== null) {
            const crop = crops[selectedCropIndex];
            if (crop.pageIndex === currentPage) {
                const handles = getHandleRects(crop.bbox, canvas.width, canvas.height);
                for (const [key, rect] of Object.entries(handles)) {
                    if (isPointInRect(x, y, rect)) {
                        setInteractionMode('resizing');
                        setActiveHandle(key as ResizeHandle);
                        setStartPos(nPos);
                        setInitialBbox(crop.bbox);
                        return;
                    }
                }
            }
        }

        // 2. Check if clicking inside a crop (Move or Select)
        // Iterate in reverse to select top-most
        for (let i = crops.length - 1; i >= 0; i--) {
            const crop = crops[i];
            if (crop.pageIndex !== currentPage) continue;

            const cx = crop.bbox.x_min * canvas.width;
            const cy = crop.bbox.y_min * canvas.height;
            const cw = (crop.bbox.x_max - crop.bbox.x_min) * canvas.width;
            const ch = (crop.bbox.y_max - crop.bbox.y_min) * canvas.height;

            if (isPointInRect(x, y, { x: cx, y: cy, w: cw, h: ch })) {
                setSelectedCropIndex(i);
                setInteractionMode('moving');
                setStartPos(nPos);
                setInitialBbox(crop.bbox);
                // Update mode to match the selected crop for UI consistency
                setMode(crop.type); 
                return;
            }
        }

        // 3. Else, Start Drawing
        setSelectedCropIndex(null);
        setInteractionMode('drawing');
        setStartPos(nPos);
        setCurrentPos(nPos);
    };

    // Mouse Move Logic
    const handleMouseMove = (e: React.MouseEvent) => {
        const nPos = getNormalizedPos(e);
        const canvas = overlayRef.current;
        
        // Update Cursor Style
        if (canvas) {
            const { x, y } = getPixelPos(e);
            let cursor = 'default';

            // Check handles
            if (selectedCropIndex !== null && crops[selectedCropIndex].pageIndex === currentPage) {
                const handles = getHandleRects(crops[selectedCropIndex].bbox, canvas.width, canvas.height);
                for (const [key, rect] of Object.entries(handles)) {
                    if (isPointInRect(x, y, rect)) {
                        cursor = `${key}-resize`;
                        break;
                    }
                }
                if (cursor === 'default') {
                    // Check inside selected box
                    const crop = crops[selectedCropIndex];
                    const cx = crop.bbox.x_min * canvas.width;
                    const cy = crop.bbox.y_min * canvas.height;
                    const cw = (crop.bbox.x_max - crop.bbox.x_min) * canvas.width;
                    const ch = (crop.bbox.y_max - crop.bbox.y_min) * canvas.height;
                    if (isPointInRect(x, y, { x: cx, y: cy, w: cw, h: ch })) {
                        cursor = 'move';
                    }
                }
            } else {
                // Check hovering over any other box
                for (let i = crops.length - 1; i >= 0; i--) {
                    const crop = crops[i];
                    if (crop.pageIndex !== currentPage) continue;
                    const cx = crop.bbox.x_min * canvas.width;
                    const cy = crop.bbox.y_min * canvas.height;
                    const cw = (crop.bbox.x_max - crop.bbox.x_min) * canvas.width;
                    const ch = (crop.bbox.y_max - crop.bbox.y_min) * canvas.height;
                    if (isPointInRect(x, y, { x: cx, y: cy, w: cw, h: ch })) {
                        cursor = 'pointer';
                        break;
                    }
                }
            }
            // If drawing/resizing/moving, override cursor
            if (interactionMode === 'drawing') cursor = 'crosshair';
            if (interactionMode === 'moving') cursor = 'move';
            if (interactionMode === 'resizing' && activeHandle) cursor = `${activeHandle}-resize`;
            
            canvas.style.cursor = cursor;
        }

        if (interactionMode === 'none' || !startPos) return;

        setCurrentPos(nPos);

        if (interactionMode === 'drawing') {
            // Only visual update handled by drawOverlay via currentPos state
        } 
        else if (interactionMode === 'moving' && selectedCropIndex !== null && initialBbox) {
            const dx = nPos.x - startPos.x;
            const dy = nPos.y - startPos.y;
            
            const newBbox = {
                x_min: initialBbox.x_min + dx,
                y_min: initialBbox.y_min + dy,
                x_max: initialBbox.x_max + dx,
                y_max: initialBbox.y_max + dy,
            };
            
            // Clamp
            // Calculate width/height to ensure we don't shrink when hitting edge
            const w = initialBbox.x_max - initialBbox.x_min;
            const h = initialBbox.y_max - initialBbox.y_min;

            if (newBbox.x_min < 0) { newBbox.x_min = 0; newBbox.x_max = w; }
            if (newBbox.y_min < 0) { newBbox.y_min = 0; newBbox.y_max = h; }
            if (newBbox.x_max > 1) { newBbox.x_max = 1; newBbox.x_min = 1 - w; }
            if (newBbox.y_max > 1) { newBbox.y_max = 1; newBbox.y_min = 1 - h; }

            const newCrops = [...crops];
            newCrops[selectedCropIndex] = { ...newCrops[selectedCropIndex], bbox: newBbox };
            setCrops(newCrops);
        }
        else if (interactionMode === 'resizing' && selectedCropIndex !== null && initialBbox && activeHandle) {
            const dx = nPos.x - startPos.x;
            const dy = nPos.y - startPos.y;
            const bb = { ...initialBbox };

            // Update edges based on handle
            if (activeHandle.includes('n')) bb.y_min += dy;
            if (activeHandle.includes('s')) bb.y_max += dy;
            if (activeHandle.includes('w')) bb.x_min += dx;
            if (activeHandle.includes('e')) bb.x_max += dx;

            // Fix negative dimensions (flip)
            const x_min = Math.min(bb.x_min, bb.x_max);
            const x_max = Math.max(bb.x_min, bb.x_max);
            const y_min = Math.min(bb.y_min, bb.y_max);
            const y_max = Math.max(bb.y_min, bb.y_max);

            const newCrops = [...crops];
            newCrops[selectedCropIndex] = { ...newCrops[selectedCropIndex], bbox: { x_min, y_min, x_max, y_max } };
            setCrops(newCrops);
        }
    };

    // Mouse Up Logic
    const handleMouseUp = () => {
        if (interactionMode === 'drawing' && startPos && currentPos) {
            const x_min = Math.max(0, Math.min(startPos.x, currentPos.x));
            const y_min = Math.max(0, Math.min(startPos.y, currentPos.y));
            const x_max = Math.min(1, Math.max(startPos.x, currentPos.x));
            const y_max = Math.min(1, Math.max(startPos.y, currentPos.y));

            // Avoid tiny clicks
            if (x_max - x_min > 0.01 && y_max - y_min > 0.01) {
                const newCrop: ManualCrop = {
                    pageIndex: currentPage,
                    bbox: { x_min, y_min, x_max, y_max },
                    type: mode,
                    label: (crops.length + 1).toString()
                };
                setCrops([...crops, newCrop]);
                setSelectedCropIndex(crops.length); // Select the newly created crop
            } else {
                // Deselect if just a click
                setSelectedCropIndex(null);
            }
        }

        setInteractionMode('none');
        setStartPos(null);
        setCurrentPos(null);
        setActiveHandle(null);
        setInitialBbox(null);
    };
    
    const undoLastCrop = () => {
        setCrops(prev => {
            const newState = prev.slice(0, -1);
            if (selectedCropIndex !== null && selectedCropIndex >= newState.length) {
                setSelectedCropIndex(null);
            }
            return newState;
        });
    };

    const clearPageCrops = () => {
        setCrops(prev => prev.filter(c => c.pageIndex !== currentPage));
        setSelectedCropIndex(null);
    };

    const deleteSelectedCrop = useCallback(() => {
        if (selectedCropIndex !== null) {
            setCrops(prev => prev.filter((_, i) => i !== selectedCropIndex));
            setSelectedCropIndex(null);
        }
    }, [selectedCropIndex]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedCropIndex !== null) {
                deleteSelectedCrop();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                undoLastCrop();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedCropIndex, deleteSelectedCrop]);

    // Wheel Navigation
    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
                if (e.deltaY > 0 && currentPage < totalPages) {
                    setCurrentPage(p => p + 1);
                } else if (e.deltaY < 0 && currentPage > 1) {
                    setCurrentPage(p => p - 1);
                }
            }
        };
        const container = containerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
        }
        return () => {
            if (container) container.removeEventListener('wheel', handleWheel);
        };
    }, [currentPage, totalPages]);

    return (
        <div className="fixed inset-0 bg-[#1A1D2D] z-[100] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-[#252A41] border-b border-[#3F3F46]">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-bold text-[var(--color-text-primary)]">영역 직접 선택</h2>
                    <div className="flex bg-black/30 rounded-lg p-1">
                        <button 
                            onClick={() => setMode('problem')}
                            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${mode === 'problem' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            문제 (파랑)
                        </button>
                        <button 
                            onClick={() => setMode('explanation')}
                            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${mode === 'explanation' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            해설 (빨강)
                        </button>
                    </div>
                    <div className="text-xs text-gray-400 ml-2 flex gap-2">
                        <span>• 선택: 클릭</span>
                        <span>• 이동: 드래그</span>
                        <span>• 삭제: Delete</span>
                        <span>• 페이지이동: Ctrl+휠</span>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button onClick={undoLastCrop} className="px-3 py-1.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 text-sm">취소 (Ctrl+Z)</button>
                    <button onClick={clearPageCrops} className="px-3 py-1.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 text-sm">페이지 초기화</button>
                    <div className="w-px h-6 bg-gray-600 mx-2"></div>
                    <button onClick={onCancel} className="px-4 py-2 text-gray-400 hover:text-white text-sm">닫기</button>
                    <button onClick={() => onFinish(crops)} className="px-6 py-2 bg-[var(--color-accent)] text-white font-bold rounded hover:bg-[var(--color-accent-hover)]">
                        {crops.length}개 영역 저장
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow flex overflow-hidden">
                {/* Main Canvas Area */}
                <div className="flex-grow overflow-auto bg-black/50 flex justify-center p-8 relative" ref={containerRef}>
                    {isLoading ? (
                        <div className="text-white self-center">PDF 로딩 중...</div>
                    ) : (
                        <div className="relative shadow-2xl" style={{ width: 'fit-content' }}>
                            <canvas ref={canvasRef} className="block bg-white" />
                            <canvas 
                                ref={overlayRef} 
                                className="absolute inset-0 touch-none"
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Navigation */}
            <div className="p-4 bg-[#252A41] border-t border-[#3F3F46] flex justify-center items-center gap-6">
                <button 
                    disabled={currentPage <= 1} 
                    onClick={() => setCurrentPage(p => p - 1)}
                    className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <span className="text-white font-mono">
                    Page {currentPage} / {totalPages}
                </span>
                <button 
                    disabled={currentPage >= totalPages} 
                    onClick={() => setCurrentPage(p => p + 1)}
                    className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
            </div>
        </div>
    );
};

export default ManualCropView;
